import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import useFlotte, {
  flotteListAtom,
  flotteMetaAtom,
} from "../../hooks/useFlotte";
import { columnsConfig } from "./flotteColumns.config";
import useDragScroll from "./hooks/useDragScroll";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";
import { isVORole, isVO531Role } from "./FlotteUtils";

// Atom de base pour stocker les colonnes modifiées par l'utilisateur
const userColumnsAtom = atom({});

// Groupes de présélection additionnels actifs ("default" toujours actif, non inclus ici)
export const activeGroupsAtom = atom([]);

// Colonne de synthèse figée (LS-707) : activée par une case à cocher,
// persistée URL + localStorage via useFlotteUrlSync (comme cols/groups)
export const stickyIdentiteAtom = atom(false);

// Atom writable avec getter et setter personnalisés
export const columnsAtom = atom(
  // Getter : combine la config de base, la visibilité dérivée des groupes actifs,
  // puis les modifications utilisateur (qui gagnent toujours)
  (get) => {
    const connectedUser = get(connectedUserAtom);
    const userColumns = get(userColumnsAtom);
    const activeGroups = get(activeGroupsAtom);
    const initialColumns = {};

    // Initialiser selon le rôle utilisateur
    Object.keys(columnsConfig).forEach((key) => {
      const col = columnsConfig[key];
      let roleVisible;
      let roleEditable;
      let included = true;

      if (isVO531Role(connectedUser?.role)) {
        included = Boolean(col.isVO531);
        roleVisible = col.visibleVO531 ?? col.visibleVO ?? col.visible;
        roleEditable = col.editableVO531 ?? col.editableVO ?? col.editable;
      } else if (isVORole(connectedUser?.role)) {
        included = Boolean(col.isVO);
        roleVisible = col.visibleVO;
        roleEditable = col.editableVO;
      } else {
        roleVisible = col.visible;
        roleEditable = col.editable;
      }

      if (!included) {
        return;
      }

      // Visibilité dérivée des groupes : le groupe "default" suit le rôle
      // (comportement inchangé) ; les autres groupes sont cumulables entre eux.
      // Une colonne peut cumuler "default" et un autre groupe (LS-699) : elle
      // suit alors le rôle ET s'affiche dès que l'un de ses groupes est actif.
      const groups = col.groups || [];
      const extraGroups = groups.filter((g) => g !== "default");
      const inActiveGroup = extraGroups.some((g) => activeGroups.includes(g));
      const groupDerivedVisible =
        groups.length === 0 || groups.includes("default")
          ? roleVisible || inActiveGroup
          : inActiveGroup;

      initialColumns[key] = {
        ...col,
        visible: groupDerivedVisible,
        editable: roleEditable,
      };
    });

    // Appliquer les modifications utilisateur par-dessus (override manuel, prioritaire)
    return { ...initialColumns, ...userColumns };
  },
  // Setter : permet de modifier les colonnes
  (get, set, newColumns) => {
    if (typeof newColumns === "function") {
      const currentColumns = get(columnsAtom);
      const updatedColumns = newColumns(currentColumns);
      set(userColumnsAtom, updatedColumns);
    } else {
      set(userColumnsAtom, newColumns);
    }
  },
);

// Retire les colonnes indiquées de la couche d'override manuel (userColumnsAtom),
// pour que la visibilité dérivée des groupes reprenne la main sur ces colonnes.
export const clearManualOverridesAtom = atom(null, (get, set, columnKeys) => {
  set(userColumnsAtom, (prev) => {
    const next = { ...prev };
    columnKeys.forEach((key) => {
      delete next[key];
    });
    return next;
  });
});

export const visibleHeadersAtom = atom((get) => {
  const columns = get(columnsAtom);
  const visibleHeaders = {};
  Object.keys(columns).forEach((key) => {
    visibleHeaders[key] = columns[key].visible;
  });
  return visibleHeaders;
});

const useFlotteTable = () => {
  const [flotteList] = useAtom(flotteListAtom);
  const [flotteMeta] = useAtom(flotteMetaAtom);
  const { actions, state } = useFlotte();
  const {
    onLoadNextPage,
    toggleRowSelection,
    toggleAllRowsSelection,
    simulerEnMasse,
    modifierSecteurEnMasse,
  } = actions;
  const {
    isLoading,
    hasMore,
    isRowSelected,
    isAllRowsSelected,
    isSomeRowsSelected,
    selectedRows,
  } = state;

  // Hook pour la fonctionnalité de drag scroll
  const { ref: tableContainerRef, isDragging } = useDragScroll();

  // États pour l'aide visuelle
  const [hoveredRowIndex, setHoveredRowIndex] = useState(-1);
  const [showSummaryCard, setShowSummaryCard] = useState(false);
  const [hoveredRowTop, setHoveredRowTop] = useState(0);

  // Colonne de synthèse figée (LS-707)
  const [stickyIdentite, setStickyIdentite] = useAtom(stickyIdentiteAtom);
  const [isScrolledX, setIsScrolledX] = useState(false);

  // Gérer le scroll horizontal pour afficher/masquer l'aide visuelle
  // et l'ombre de la zone figée
  useEffect(() => {
    const handleScroll = () => {
      if (tableContainerRef.current) {
        const scrollLeftValue = tableContainerRef.current.scrollLeft;
        setShowSummaryCard(scrollLeftValue > 500);
        setIsScrolledX(scrollLeftValue > 0);
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [tableContainerRef]);

  // Position left de la colonne de synthèse figée = largeur réelle de la
  // colonne de sélection (1re colonne), qui varie selon les badges affichés
  useEffect(() => {
    if (!stickyIdentite) return;
    const container = tableContainerRef.current;
    if (!container) return;
    const firstTh = container.querySelector("thead th:first-child");
    if (!firstTh) return;

    const applyOffset = () => {
      container.style.setProperty(
        "--identite-left",
        `${firstTh.offsetWidth}px`,
      );
    };
    applyOffset();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(applyOffset);
    observer.observe(firstTh);
    return () => observer.disconnect();
  }, [stickyIdentite, tableContainerRef]);

  // Utilisation de l'atom pour gérer la visibilité des colonnes
  const [columns, setColumns] = useAtom(columnsAtom);

  const nbCells = useMemo(() => {
    return (
      Object.keys(columns).filter((key) => columns[key].visible).length +
      (stickyIdentite ? 1 : 0)
    );
  }, [columns, stickyIdentite]);

  // Fonction pour mettre à jour plusieurs colonnes (compatible avec setVisibleHeaders)
  const setVisibleHeaders = (updater) => {
    const visibleAttribute = "visible";
    if (typeof updater === "function") {
      setColumns((prev) => {
        const currentHeaders = {};
        Object.keys(prev).forEach((key) => {
          currentHeaders[key] = prev[key][visibleAttribute];
        });
        const newHeaders = updater(currentHeaders);
        const newColumns = { ...prev };
        Object.keys(newHeaders).forEach((key) => {
          if (newColumns[key]) {
            newColumns[key] = {
              ...newColumns[key],
              [visibleAttribute]: newHeaders[key],
            };
          }
        });
        console.log("newColumns", newColumns);
        return newColumns;
      });
    } else {
      // Si c'est un objet direct
      setColumns((prev) => {
        const newColumns = { ...prev };
        Object.keys(updater).forEach((key) => {
          if (newColumns[key]) {
            newColumns[key] = {
              ...newColumns[key],
              [visibleAttribute]: updater[key],
            };
          }
        });
        console.log("newColumns", newColumns);
        return newColumns;
      });
    }
  };

  // Alternative à l'IntersectionObserver : détecter le scroll en bas du conteneur
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const bottomThreshold = 200; // Distance du bas pour déclencher

      // Calculer si on est proche du bas
      const isNearBottom =
        scrollTop + clientHeight >= scrollHeight - bottomThreshold;

      if (isNearBottom && !isLoading && hasMore) {
        console.log(
          "🔥 Infinite scroll triggered via scroll event - Loading next page",
        );
        onLoadNextPage();
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading, hasMore, onLoadNextPage, tableContainerRef]);

  // Callback pour la référence de la dernière ligne (plus utilisé avec l'approche scroll)
  const lastRowRefCallback = useCallback((node) => {
    // Plus besoin d'IntersectionObserver, mais on garde le callback pour compatibilité
    return node;
  }, []);

  // Plus besoin de nettoyer l'observateur car on utilise maintenant les événements scroll

  // Fonction pour gérer le hover sur une ligne
  const handleRowMouseEnter = useCallback((index, event) => {
    setHoveredRowIndex(index);
    if (event?.currentTarget) {
      const rowRect = event.currentTarget.getBoundingClientRect();
      // Position fixe par rapport au viewport
      setHoveredRowTop(rowRect.top + window.scrollY);
    }
  }, []);

  return {
    flotteList,
    flotteMeta,
    isLoading,
    hasMore,
    tableContainerRef,
    lastRowRefCallback,
    isDragging,
    hoveredRowIndex,
    showSummaryCard,
    hoveredRowTop,
    nbCells,
    // Colonne de synthèse figée (LS-707)
    stickyIdentite,
    setStickyIdentite,
    isScrolledX,
    setVisibleHeaders,
    handleRowMouseEnter,
    setHoveredRowIndex,
    // Fonctions de sélection
    toggleRowSelection,
    toggleAllRowsSelection,
    isRowSelected,
    isAllRowsSelected,
    isSomeRowsSelected,
    // Actions en masse
    selectedRows,
    simulerEnMasse,
    modifierSecteurEnMasse,
  };
};

export default useFlotteTable;
